import { Button, Card, Flex, Text } from '@chakra-ui/react';
import { useTranslation } from 'react-i18next';
import { useMemo, useRef } from 'react';
import { ConfirmModal, createDataCollectionContext, dbTimeToUserTime, toaster, useMobileContext, useValidatedSorting, type ConfirmModalHandle, type ItemAction } from '@shared';
import { CustomFieldsService, useDeleteMultipleCustomFields, useDrawer } from '@admin';
import { createColumnHelper } from '@tanstack/react-table';
import { useQueryClient } from '@tanstack/react-query';
import CustomFieldDrawer from './CustomFieldDrawer';
import { IoExtensionPuzzleOutline } from 'react-icons/io5';

const columnHelper = createColumnHelper<CustomField>();
const TIMEZONE = import.meta.env.VITE_DEFAULT_TIMEZONE as string;

export default function CustomFields() {
	const { t } = useTranslation();
	const queryClient = useQueryClient();
	const { isMobile } = useMobileContext();

	const { DataCollectionContextProvider, DataCollectionTable, DataCollectionTableFooter, DataCollectionTableHeader } = useMemo(
		() => createDataCollectionContext<CustomField>(),
		[],
	);

	const columns = [
		columnHelper.accessor('label', {
			header: t('general.name'),
			sortingFn: 'basic',
		}),

		columnHelper.accessor('timeCreated', {
			header: t('general.time_created'),
			sortingFn: 'basic',
			cell: ({ row }) => {
				return <Text>{dbTimeToUserTime(row.original.timeCreated, TIMEZONE)}</Text>;
			},
		}),
		columnHelper.accessor('timeModified', {
			header: t('general.time_modified'),
			sortingFn: 'basic',
			cell: ({ row }) => {
				if (!row.original.timeModified) {
					return null;
				}
				return <Text>{dbTimeToUserTime(row.original.timeModified, TIMEZONE)}</Text>;
			},
		}),
	];

	useValidatedSorting('customFields', columns);

	const { mutate: deleteMultipleCustomFields } = useDeleteMultipleCustomFields();

	const confirmRemoveRef = useRef<ConfirmModalHandle>(null);

	const selectedItemsActions: Array<ItemAction<CustomField>> = [
		{
			id: 'deleteMany',
			label: t('general.delete_selected'),
			callbackFn: async (ids, table) => {
				const payload = {
					customFields: ids.map(id => {
						return {
							customFieldID: parseInt(id),
							delete: true,
						};
					}),
				};

				const response = await confirmRemoveRef.current?.open({
					title: t('general.remove_custom_fields'),
					content: t('general.really_remove_custom_fields'),
					confirmButtonText: t('general.remove'),
					confirmButtonVariant: 'subtle',
				});

				if (response === true) {
					deleteMultipleCustomFields(payload, {
						onSuccess: () => {
							toaster.create({
								type: 'success',
								title: t('general.removed'),
							});
							queryClient.invalidateQueries({ queryKey: ['customFields'] });
							table.resetRowSelection();
						},
					});
				}
			},
		},
	];

	const { openDrawer } = useDrawer();

	return (
		<Card.Root flex={1} display="flex">
			<Card.Body display="flex" flexDirection="column" padding={5} gap={5} overflow={isMobile ? 'initial' : 'auto'}>
				<DataCollectionContextProvider
					dataKey="customFields"
					keyAttribute="customFieldID"
					emptyDataText={t('general.no_custom_fields_found')}
					emptyDataIcon={IoExtensionPuzzleOutline}
					fetchFn={(...args) => CustomFieldsService.getAllResources(...args)}
					selectedItemsActions={selectedItemsActions}
					initialItemsPerPage={25}
					columns={columns}
					defaultHiddenColumns={[]}
					isServerSide={true}
					isSelectable={true}
					defaultSort={{ id: 'timeCreated', desc: true }}
					onClickRow={row => {
						openDrawer({
							id: 'customFieldDrawer',
							content: <CustomFieldDrawer customFieldID={row.original.customFieldID} />,
						});
					}}
				>
					<Flex gap={2} flexWrap={'wrap'}>
						<DataCollectionTableHeader />
						<Button
							data-cy="addCustomFieldButton"
							onClick={() => {
								openDrawer({
									id: 'customFieldDrawer',
									content: <CustomFieldDrawer />,
								});
							}}
						>
							{t('general.add_custom_field')}
						</Button>
					</Flex>
					<DataCollectionTable />
					<Flex gap={2}>
						<DataCollectionTableFooter />
						<ConfirmModal ref={confirmRemoveRef} />
					</Flex>
				</DataCollectionContextProvider>
			</Card.Body>
		</Card.Root>
	);
}
